/*
 * useRosterView — pulls the four memos that dominate Shifts.jsx state
 * derivation into a single custom hook. Same behaviour, half the lines
 * in the parent.
 *
 * Returns:
 *   visibleSites          — filtered + sorted site rows for the grid
 *   visibleShifts         — shifts filtered by status
 *   cellMap               — { `${site_id}|${day.toDateString()}`: [shift, ...] }
 *   conflictIds           — Set<shift_id> of -overlap conflicts
 *   vacationConflictIds   — Set<shift_id> of shifts hitting an approved vacation
 *                           (per-day, sourced from each shift's
 *                           `unavailable_guards` annotation — NOT the parent
 *                           vacation_request range, so per-day carve-outs on
 *                           the Availability page take effect immediately).
 */
import { useMemo } from "react";

export function useRosterView({
  shifts,
  sites,
  clientFilter,
  siteFilter,
  siteStatusFilter,
  shiftStatusFilter,
  days,
  hideEmptySites,
  needsCoverageOnly,
  shortRestHours = 6,
}) {
  const visibleSites = useMemo(() => {
    const siteSet = new Set(siteFilter);
    const cf = new Set(clientFilter);
    const filtered = sites.filter((s) => {
      if (cf.size > 0 && !cf.has(s.client_name)) return false;
      if (siteSet.size > 0 && !siteSet.has(s.id)) return false;
      if (siteStatusFilter.size && !siteStatusFilter.has(s.status || "active")) return false;
      return true;
    });
    const nowMs = Date.now();
    const todayKey = new Date().toDateString();
    const rangeStartMs = days.length ? new Date(days[0]).setHours(0, 0, 0, 0) : 0;
    const rangeEndMs = days.length
      ? new Date(days[days.length - 1]).setHours(23, 59, 59, 999)
      : Infinity;
    const sitesWithShiftInRange = new Set();
    const sitesNeedingCoverage = new Set();
    const stats = new Map();
    for (const sh of shifts) {
      if (!sh.start_time || !sh.site_id) continue;
      const d = new Date(sh.start_time);
      const startMs = d.getTime();
      if (!Number.isFinite(startMs)) continue;
      if (startMs >= rangeStartMs && startMs <= rangeEndMs) {
        sitesWithShiftInRange.add(sh.site_id);
        const assigned = (sh.guard_ids || []).length;
        const isOpen = sh.is_open || assigned === 0;
        const isUnderstaffed = sh.is_understaffed
          || (assigned > 0 && sh.min_guards && assigned < sh.min_guards);
        if (isOpen || isUnderstaffed) sitesNeedingCoverage.add(sh.site_id);
      }
      const isToday = d.toDateString() === todayKey;
      const isFuture = startMs > nowMs;
      const cur = stats.get(sh.site_id) || {
        earliestToday: Infinity, earliestFuture: Infinity, latestPast: -Infinity,
      };
      if (isToday && startMs < cur.earliestToday) cur.earliestToday = startMs;
      if (isFuture && startMs < cur.earliestFuture) cur.earliestFuture = startMs;
      if (!isFuture && !isToday && startMs > cur.latestPast) cur.latestPast = startMs;
      stats.set(sh.site_id, cur);
    }
    const withInRangeFilter = hideEmptySites
      ? filtered.filter((s) => sitesWithShiftInRange.has(s.id))
      : filtered;
    const withCoverageFilter = needsCoverageOnly
      ? withInRangeFilter.filter((s) => sitesNeedingCoverage.has(s.id))
      : withInRangeFilter;
    const rank = (r) => {
      if (r.earliestToday !== Infinity) return 0;
      if (r.earliestFuture !== Infinity) return 1;
      if (r.latestPast !== -Infinity) return 2;
      return 3;
    };
    const EMPTY = { earliestToday: Infinity, earliestFuture: Infinity, latestPast: -Infinity };
    return [...withCoverageFilter].sort((a, b) => {
      const A = stats.get(a.id) || EMPTY;
      const B = stats.get(b.id) || EMPTY;
      const rA = rank(A); const rB = rank(B);
      if (rA !== rB) return rA - rB;
      if (rA === 0) return A.earliestToday - B.earliestToday;
      if (rA === 1) return A.earliestFuture - B.earliestFuture;
      if (rA === 2) return B.latestPast - A.latestPast;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [sites, shifts, clientFilter, siteFilter, siteStatusFilter, days, hideEmptySites, needsCoverageOnly]);

  const visibleShifts = useMemo(
    () => shifts.filter((s) => shiftStatusFilter.has(s.status || "scheduled")),
    [shifts, shiftStatusFilter]
  );

  const cellMap = useMemo(() => {
    const m = {};
    visibleShifts.forEach((s) => {
      const k = `${s.site_id}|${new Date(s.start_time).toDateString()}`;
      (m[k] = m[k] || []).push(s);
    });
    return m;
  }, [visibleShifts]);

  const conflictIds = useMemo(() => {
    const conflicts = new Set();
    const byGuard = {};
    shifts.forEach((s) => {
      const ids = (s.guard_ids && s.guard_ids.length) ? s.guard_ids : (s.guard_id ? [s.guard_id] : []);
      ids.forEach((gid) => { (byGuard[gid] = byGuard[gid] || []).push(s); });
    });
    Object.values(byGuard).forEach((arr) => {
      for (let i = 0; i < arr.length; i++) {
        for (let j = i + 1; j < arr.length; j++) {
          if (arr[i].id === arr[j].id) continue;
          const aS = new Date(arr[i].start_time).getTime();
          const aE = new Date(arr[i].end_time).getTime();
          const bS = new Date(arr[j].start_time).getTime();
          const bE = new Date(arr[j].end_time).getTime();
          if (aS < bE && bS < aE) { conflicts.add(arr[i].id); conflicts.add(arr[j].id); }
        }
      }
    });
    return conflicts;
  }, [shifts]);

  // Vacation conflicts are computed strictly per-day from each shift's
  // `unavailable_guards` annotation (backend-supplied, sourced from the
  // day-level availability collection). This means a supervisor who splits
  // a vacation range and carves out one specific day via the Availability
  // page will see that day's shift automatically drop off the vacation-
  // conflict flag — even though the parent `vacation_requests` record still
  // covers the whole original range. The parent record is intentionally
  // NOT consulted here; each day is evaluated independently.
  //
  // Source values on the availability rows: `vacation`, `vacation_auto`
  // (materialized on approval), and future variants. Match any `vacation*`
  // prefix so all vacation-derived rows count as a vacation conflict.
  const vacationConflictIds = useMemo(() => {
    const hit = new Set();
    shifts.forEach((s) => {
      const unavail = Array.isArray(s.unavailable_guards) ? s.unavailable_guards : [];
      if (unavail.some((u) => typeof u?.source === "string" && u.source.startsWith("vacation"))) {
        hit.add(s.id);
      }
    });
    return hit;
  }, [shifts]);

  // iter325 — Short-rest / turnaround detection. Flags any shift whose
  // guard has another shift ending less than the agency-configured
  // number of hours before this one starts (iter327). 0 disables the
  // warning entirely. iter324's ±1 day padding on the Roster fetch
  // means both shifts of a short-rest pair are always in scope
  // whenever either one is visible (max 12h < 24h padding).
  const shortRest = useMemo(() => {
    const flagged = new Map(); // shift_id -> minutes of rest (integer)
    if (!shortRestHours || shortRestHours <= 0) return flagged;
    const thresholdMs = shortRestHours * 60 * 60 * 1000;
    const byGuard = {};
    shifts.forEach((s) => {
      if (!s.start_time || !s.end_time) return;
      const ids = (s.guard_ids && s.guard_ids.length) ? s.guard_ids : (s.guard_id ? [s.guard_id] : []);
      ids.forEach((gid) => { (byGuard[gid] = byGuard[gid] || []).push(s); });
    });
    Object.values(byGuard).forEach((arr) => {
      const sorted = [...arr].sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      for (let i = 1; i < sorted.length; i++) {
        const prevEnd = new Date(sorted[i - 1].end_time).getTime();
        const thisStart = new Date(sorted[i].start_time).getTime();
        if (!Number.isFinite(prevEnd) || !Number.isFinite(thisStart)) continue;
        const gap = thisStart - prevEnd;
        // Ignore overlaps (that's already surfaced by conflictIds) and
        // gaps at or beyond the threshold.
        if (gap >= 0 && gap < thresholdMs) {
          const minutes = Math.round(gap / 60000);
          // Keep the tightest turnaround if a guard has multiple.
          const existing = flagged.get(sorted[i].id);
          if (existing === undefined || minutes < existing) {
            flagged.set(sorted[i].id, minutes);
          }
        }
      }
    });
    return flagged;
  }, [shifts, shortRestHours]);

  return { visibleSites, visibleShifts, cellMap, conflictIds, vacationConflictIds, shortRest };
}
